// src/components/SettingsNav.js
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LuSettings,
  LuBuilding,
  LuUser,
  LuUsers,
  LuKey,
  LuCreditCard,
  LuFileText,
} from "react-icons/lu";
import "../styles/SettingsNav.css";
import api from "../axiosConfig"; // ⬅️ NEW: fetch authoritative matrix
import { useTranslation } from "react-i18next";

/* ---------- permission helpers (mirror Sidebar) ---------- */
const MATRIX_STORAGE_KEY = "permissions.matrix.v1";

const readJSON = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "{}"); }
  catch { return {}; }
};

const readPermMatrix = () => {
  try { return JSON.parse(localStorage.getItem(MATRIX_STORAGE_KEY) || "{}"); }
  catch { return {}; }
};

// Robust role getter (fallback to 'user' if 'auth_user' missing role)
const getRole = () => {
  const au = readJSON("auth_user");
  const u  = readJSON("user");
  return (au.role || u.role || "").toLowerCase();
};

/**
 * can()
 * - If no matrix cached at all → allow (bootstrap state before perms are fetched)
 * - If matrix exists but role/permKey missing → deny (don’t show tabs)
 */
const can = (role, key) => {
  const m = readPermMatrix();
  const hasMatrix = m && Object.keys(m).length > 0;
  if (!hasMatrix) return true;
  if (!role || !m[role]) return false;
  const v = m[role][key];
  return typeof v === "boolean" ? v : false;
};

/* ---------- map each tab to a permission key ---------- */
const items = [
  { to: "general",     label: "General",               icon: LuSettings,   permKey: "generalSettings" },
  { to: "company",     label: "Company",               icon: LuBuilding,   permKey: "companySettings" },
  { to: "profile",     label: "Profile",               icon: LuUser,       permKey: "profileSettings" },
  { to: "users",       label: "Users",                 icon: LuUsers,      permKey: "usersSettings" },
  { to: "permissions", label: "Permissions",           icon: LuKey,        permKey: "permissionsSettings" },
];

export default function SettingsNav() {
  const { t } = useTranslation("settings_nav");

  const [role, setRole] = useState(() => getRole());
  const [, setTick] = useState(0); // force re-render when matrix changes

  useEffect(() => {
    // ⬅️ NEW: always fetch the authoritative matrix on mount
    (async () => {
      try {
        const { data } = await api.get("/permissions/matrix", {
          headers: { "Cache-Control": "no-store" },
        });
        if (data?.matrix) {
          localStorage.setItem(MATRIX_STORAGE_KEY, JSON.stringify(data.matrix));
          // force redraw after fresh cache lands
          setTick((t) => t + 1);
        }
      } catch (e) {
        console.warn("[settingsnav] GET /permissions/matrix failed", e?.response?.data || e?.message || e);
      }
    })();

    // reflect role changes from other tabs / profile updates
    const onStorage = (e) => {
      if (e.key === "auth_user" || e.key === "user") setRole(getRole());
      // ⬅️ NEW: react if another tab writes a fresher matrix
      if (e.key === MATRIX_STORAGE_KEY) setTick((t) => t + 1);
    };
    window.addEventListener("storage", onStorage);

    // re-render when the permissions matrix changes in this tab
    const onPermsChanged = () => setTick((t) => t + 1);
    window.addEventListener("permissions:changed", onPermsChanged);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("permissions:changed", onPermsChanged);
    };
  }, []);

  const visibleItems = items.filter((it) => can(role, it.permKey));

  // map route "to" -> translation key; fallback to original label via t's defaultValue
  const labelKeyByTo = {
    general: "items.general",
    company: "items.company",
    profile: "items.profile",
    users: "items.users",
    permissions: "items.permissions",
    plans: "items.plans",
  };

  return (
    <aside className="settings-nav">
      <div className="settings-nav__inner">
        <div className="settings-nav__title">{t("title")}</div>
        <ul className="settings-nav__list">
          {visibleItems.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end
                className={({ isActive }) =>
                  "settings-link" + (isActive ? " settings-link--active" : "")
                }
              >
                <Icon className="settings-link__icon" />
                <span className="settings-link__text">
                  {t(labelKeyByTo[to], label)}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
        {visibleItems.length === 0 && (
          <div className="settings-nav__empty">{t("empty")}</div>
        )}
      </div>
    </aside>
  );
}
